import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  Icon,
  MediaObject,
  Modal,
  Paragraph,
  Tag,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconDollarSignCircleFilled, IconErrorFilled, IconHouseHeartFilled } from '@zillow/constellation-icons';
import CoinIcon from '../images/CoinIcon';
import ZillowHomeLoansLogo from '../images/ZillowHomeLoansLogo';

export type RewardsEnrollmentModalProps = {
  /**
   * Whether the modal is open. Controls visibility externally.
   */
  open: boolean;
  /**
   * Called when the modal open state changes (e.g. user clicks close or backdrop).
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Called when the user clicks the join CTA.
   */
  onEnroll: () => void;
  /**
   * Whether the user pays rent through Zillow Rent Payment. Rent Payment renters enroll in one step
   * ("Join instantly"); Plaid users see "Join now".
   * @default false
   */
  isRentPaymentRenter?: boolean;
  /**
   * Number of bonus points the user can claim. Provided by the backend API.
   * Displayed in the footer as "Claim {points} bonus points when you join".
   */
  claimablePoints: number;
  /**
   * Maximum dollar amount the user can earn. Displayed in the hero headline.
   * @default 4000
   */
  maxDollarValue?: number;
  /**
   * Whether the enroll action is currently loading.
   */
  isLoading?: boolean;
  /**
   * URL for the Zillow Rewards Terms link.
   */
  termsUrl?: string;
  /**
   * URL for the Notice of Financial Incentive link.
   */
  financialIncentiveUrl?: string;
  /**
   * URL for the Privacy Policy link.
   */
  privacyPolicyUrl?: string;
  /**
   * URL for the Zillow Home Loans link.
   */
  zillowHomeLoansUrl?: string;
  /**
   * Panda CSS theme applied to the modal backdrop.
   * @default 'zillow'
   */
  theme?: 'zillow' | 'legacy-zillow';
  /**
   * Whether the enrollment API call failed. When true, the modal displays
   * an inline error message instead of the normal enrollment content.
   */
  hasEnrollmentError?: boolean;
  /**
   * URL for the "Contact care" link shown in the error state footer.
   */
  contactCareUrl?: string;
};

export default function RewardsEnrollmentModal({
  open,
  onOpenChange,
  onEnroll,
  isRentPaymentRenter = false,
  claimablePoints,
  maxDollarValue = 4000,
  isLoading = false,
  termsUrl = 'https://www.zillow.com/rent/rewards-terms/',
  financialIncentiveUrl = 'https://www.zillow.com/rent/rewards-terms/#heading-notice-of-financial-incentive',
  privacyPolicyUrl = 'https://www.zillow.com/corporate/zg-privacy-notice/',
  zillowHomeLoansUrl = 'https://www.zillow.com/homeloans/',
  theme = 'zillow',
  hasEnrollmentError = false,
  contactCareUrl,
}: RewardsEnrollmentModalProps) {
  const joinLabel = isRentPaymentRenter ? 'Join instantly' : 'Join now';

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} size={{ base: 'fullScreen', lg: 'lg' }}>
      <Modal.Portal>
        <Modal.Backdrop data-panda-theme={theme}>
          <Modal.Content>
            <Modal.Header>
              <Heading level={2}>Zillow rewards</Heading>
            </Modal.Header>

            {hasEnrollmentError ? (
              <>
                <Divider />
                <Modal.Body>
                  <Box role="alert" css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
                    <Icon render={<IconErrorFilled />} size="lg" css={{ color: 'icon.feedback.critical.hero-alt' }} />
                    <Heading level={3} css={{ textStyle: 'heading-sm' }}>
                      There was an error during enrollment
                    </Heading>
                    <Paragraph>
                      We were unable to enroll you in Zillow rewards. You can try refreshing the page. If the problem
                      persists, please contact customer care.
                    </Paragraph>
                  </Box>
                </Modal.Body>

                <Divider />

                <Modal.Footer>
                  <ButtonGroup aria-label="Enrollment error actions">
                    {contactCareUrl && (
                      <TextButton asChild tone="brand">
                        <a href={contactCareUrl} target="_blank" rel="noopener noreferrer">
                          Contact care
                        </a>
                      </TextButton>
                    )}
                    <Button tone="brand" emphasis="filled" onClick={() => onOpenChange(false)}>
                      Close
                    </Button>
                  </ButtonGroup>
                </Modal.Footer>
              </>
            ) : (
              <>
                {/* Hero Section — edge-to-edge, outside Modal.Body per design */}
                <Box
                  css={{
                    backgroundColor: 'bg.express.inspire.impact',
                    padding: 'looser',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'tight',
                  }}
                >
                  <Tag tone="purple" size="sm" css={{ width: 'fit-content' }}>
                    Free to join
                  </Tag>
                  <Heading level={3} css={{ textStyle: 'heading-sm', color: 'text.onHero.neutral' }}>
                    Your rent could earn you up to{' '}
                    <Box asChild css={{ color: 'text.onImpact.express.inspire.hero' }}>
                      <span>${maxDollarValue.toLocaleString()}</span>
                    </Box>{' '}
                    toward a home purchase
                  </Heading>
                  <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                    <Paragraph textStyle="body-bold" css={{ color: 'text.onHero.neutral' }}>
                      When financing with
                    </Paragraph>
                    <Box
                      css={{
                        color: 'text.onHero.neutral',
                        display: { base: 'none', lg: 'flex' },
                        alignItems: 'center',
                      }}
                    >
                      <ZillowHomeLoansLogo />
                    </Box>
                    <Paragraph
                      aria-hidden="true"
                      textStyle="body-bold"
                      css={{
                        color: 'text.onHero.neutral',
                        display: { base: 'inline', lg: 'none' },
                      }}
                    >
                      Zillow Home Loans
                    </Paragraph>
                  </Box>
                </Box>

                {/* Benefits Section */}
                <Modal.Body>
                  <Box css={{ display: 'flex', flexDirection: 'column', gap: 'looser', padding: 'tight' }}>
                    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
                      <MediaObject
                        media={
                          <Icon
                            render={<IconDollarSignCircleFilled />}
                            size="md"
                            css={{ color: 'icon.express.inspire.hero' }}
                          />
                        }
                      >
                        <Paragraph textStyle="body-bold">Pay rent, earn points</Paragraph>
                        <Paragraph textStyle="body-sm">
                          Get 100 points for every future rent payment. Each point can be redeemed for $1 off closing
                          costs.
                        </Paragraph>
                      </MediaObject>

                      <MediaObject
                        media={
                          <Icon
                            render={<IconHouseHeartFilled />}
                            size="md"
                            css={{ color: 'icon.express.inspire.hero' }}
                          />
                        }
                      >
                        <Paragraph textStyle="body-bold">Redeem points when you buy</Paragraph>
                        <Paragraph textStyle="body-sm">
                          Easily find a Zillow partner agent and learn how to finance your home with{' '}
                          <Anchor href={zillowHomeLoansUrl} target="_blank" rel="noopener noreferrer">
                            Zillow Home Loans
                          </Anchor>
                          .
                        </Paragraph>
                      </MediaObject>
                    </Box>

                    <Paragraph textStyle="fineprint-sm" css={{ color: 'text.subtle' }}>
                      By selecting <Text textStyle="fineprint-sm-bold">{joinLabel}</Text>, you agree to the{' '}
                      <Anchor href={termsUrl} target="_blank" rel="noopener noreferrer">
                        Zillow Rewards Terms
                      </Anchor>
                      , including the{' '}
                      <Anchor href={financialIncentiveUrl} target="_blank" rel="noopener noreferrer">
                        Notice of Financial Incentive
                      </Anchor>
                      , and acknowledge the{' '}
                      <Anchor href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </Anchor>
                      . Points have no cash value and are redeemable only as a lender credit when you finance with
                      Zillow Home Loans. Loan approval and closing required.
                    </Paragraph>
                  </Box>
                </Modal.Body>

                <Divider />

                <Modal.Footer css={{ paddingBlock: 'loose' }}>
                  <Box
                    css={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 'default',
                      width: '100%',
                    }}
                  >
                    {claimablePoints > 0 && (
                      <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                        <CoinIcon />
                        <Text textStyle="body-sm">
                          Claim <Text textStyle="body-sm-bold">{claimablePoints} bonus points</Text>
                          <Text css={{ display: { base: 'none', lg: 'inline' } }}> when you join</Text>
                        </Text>
                      </Box>
                    )}
                    <Button tone="brand" emphasis="filled" onClick={onEnroll} loading={isLoading}>
                      {joinLabel}
                    </Button>
                  </Box>
                </Modal.Footer>
              </>
            )}
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
